import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import numeral from 'numeral';
import moment from 'moment';
import generateUuid from 'uuid';
import { calculateQuote } from 'actions/quotes';
import _ from 'lodash';

const styleSheet = (theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    flexGrow: 1,
  },
  paper: {
    padding: 16
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
    width: 180,
  },
  button: {
    marginLeft: 50,
    alignSelf: 'center',
    height: 20,
  }
});

class Calculator extends React.Component {
  constructor(props) {
    super(props);
    const form = props.application && props.application.form;
    const drawdownDate = _.get(form, 'drawdownDate') ? moment(_.get(form, 'drawdownDate')).format('YYYY-MM-DD') : moment().add(3, 'days').format('YYYY-MM-DD');
    this.state = {
      calculating: false,
      principal: _.get(form, 'amount') || 150000,
      interestRate: 20,
      termLength: _.get(form, 'termLength') || 5,
      termUnit: _.get(form, 'termUnit') || 'month',
      drawdownDate,
      establishmentFee: 3374.23,
      repaymentFrequency: _.get(form, 'frequency') || 'month',
      schedules: null,
      readonly: !!form,
    }
  };

  renderSchedule = (key, schedule) => {
    const repaymentDate = moment(key, 'YYYY-MM-DD').format(' YYYY-MM-DD dddd');
    return (
      <TableRow key={key}>
        <TableCell>
          {repaymentDate}
        </TableCell>
        <TableCell>
          {schedule.days}
        </TableCell>
        <TableCell>
          <strong>{numeral(schedule.total).format('$0,0.00')}</strong>
        </TableCell>
        <TableCell>
          {numeral(schedule.principal).format('$0,0.00')}
        </TableCell>
        <TableCell>
          {numeral(schedule.interest).format('$0,0.00')}
        </TableCell>
        <TableCell>
          {numeral(schedule.establishmentFee).format('$0,0.00')}
        </TableCell>
      </TableRow>
    );
  }

  renderMetadata = () => {
    const { totalInterest, totalRepayment } = this.quote();
    return (
      <div>
        <div>
          <Typography variant="headline" gutterBottom>
            Result
          </Typography>
          <Divider light />
        </div>
        <List>
          <ListItem>
            <ListItemText primary={ `Total interest: ${numeral(totalInterest).format('$0,0.00')}` } />
          </ListItem>
          <ListItem>
            <ListItemText primary={ `Total payable: ${numeral(totalRepayment).format('$0,0.00')}` }/>
          </ListItem>
        </List>
      </div>
    )
  }

  quote = () => {
    const { quotes } = this.props;
    const { uuid } = this.state;
    if (uuid && quotes) {
      this.props.setQuoteUuid && this.props.setQuoteUuid(uuid);
      return quotes[uuid];
    } else {
      return null;
    }
  }

  calc = () => {
    const { dispatch } = this.props;
    const uuid = generateUuid();
    this.setState({ uuid, schedules: null, calculating: true });
    dispatch(calculateQuote({ ...this.state, uuid }))
      .then(() => this.setState({ calculating: false }));
  }

  renderSchedules = () => {
    const { schedules } = this.quote();

    return Object
      .keys(schedules)
      .map(key => this.renderSchedule(key, schedules[key]));
  }

  render = () => {
    const classes = this.props.classes;
    const { calculating } = this.state;

    return (
      <div className={classes.root}>
        <Typography variant="headline" gutterBottom>
          Calculator
        </Typography>
        <Divider light />
        <div className={classes.container}>
          <TextField
            id="principal"
            label="Principal"
            className={classes.textField}
            value={this.state.principal}
            onChange={event => this.setState({ principal: event.target.value })}
            margin="normal"
            type="number"
            disabled={this.state.readonly}
          />
          <TextField
            id="interest_rate"
            label="Interest rate (%)"
            className={classes.textField}
            value={this.state.interestRate}
            onChange={event => this.setState({ interestRate: event.target.value })}
            margin="normal"
            type="number"
          />
          <TextField
            id="term_length"
            label="Term duration"
            className={classes.textField}
            value={this.state.termLength}
            onChange={event => this.setState({ termLength: event.target.value })}
            margin="normal"
            type="number"
            disabled={this.state.readonly}
          />
          <TextField
            select
            id="term_unit"
            label="Day/week/month"
            className={classes.textField}
            value={this.state.termUnit}
            onChange={event => this.setState({ termUnit: event.target.value })}
            margin="normal"
            disabled={this.state.readonly}
          >
            <MenuItem key={'day'} value={'day'}>Day</MenuItem>
            <MenuItem key={'week'} value={'week'}>Week</MenuItem>
            <MenuItem key={'month'} value={'month'}>Month</MenuItem>
          </TextField>
          <TextField
            id="drawdown_date"
            label="Drawdown date"
            className={classes.textField}
            value={this.state.drawdownDate}
            onChange={event => this.setState({ drawdownDate: event.target.value })}
            margin="normal"
            type="date"
            disabled={this.state.readonly}
          />
          <TextField
            id="establishment_fee"
            label="Establishment fee"
            className={classes.textField}
            value={this.state.establishmentFee}
            onChange={event => this.setState({ establishmentFee: event.target.value })}
            margin="normal"
            type="number"
          />
          <TextField
            select
            id="repayment_frequency"
            label="Repayment"
            className={classes.textField}
            value={this.state.repaymentFrequency}
            onChange={event => this.setState({ repaymentFrequency: event.target.value })}
            margin="normal"
          >
            <MenuItem key={'week'} value={'week'}>Week</MenuItem>
            <MenuItem key={'month'} value={'month'}>Month</MenuItem>
          </TextField>
          <Button variant='raised' color="primary" className={classes.button} onClick={this.calc}>
            Calculate
          </Button>
        </div>
        <br/>
        {
          this.quote() && this.renderMetadata()
        }
        {
          calculating && <div style={{textAlign: 'center'}}>
            <CircularProgress color='primary' size={70} />
          </div>
        }
        {
          this.quote() && (
            <div>
              <Typography variant="subheading" gutterBottom>
                Schedule
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Repayment date</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell>Total payable</TableCell>
                    <TableCell>Principal</TableCell>
                    <TableCell>Interest</TableCell>
                    <TableCell>Establishment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {this.renderSchedules()}
                </TableBody>
              </Table>
            </div>
          )
        }
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { quotes, app: api } = state;
  const application = state.applications && state.applications[ownProps.applicationNumber];

  return { quotes, api, application };
};

const reduxed = connect(mapStateToProps)(Calculator)
export default withStyles(styleSheet)(reduxed)



// WEBPACK FOOTER //
// ./src/components/Loans/Calculator.js